import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import axios from 'axios';
import config from '../config';

// Excel column names
const REQUIRED_COLUMNS = [
  'Name',
  'Fulladdress',
  'Street',
  'Municipality',
  'Categories',
  'Phone',
  'Phones',
  'Claimed',
  'Review Count',
  'Average Rating',
  'Review URL',
  'Google Maps URL',
  'Latitude',
  'Longitude',
  'Website',
  'Domain',
  'Opening hours',
  'Featured image',
  'Cid',
  'Place Id',
  'Kgmid',
  'Plus code',
  'Google Knowledge URL',
  'Email',
  'Social Medias',
  'Facebook',
  'Instagram',
  'Twitter',
  'Yelp'
];

// Define the structure for lead data from Excel
interface LeadData {
  Name: string;
  Fulladdress: string;
  Street?: string;
  Municipality?: string;
  Categories?: string;
  Phone?: string;
  Phones?: string;
  Claimed?: 'YES' | 'NO';
  'Review Count'?: number;
  'Average Rating'?: number;
  'Review URL'?: string;
  'Google Maps URL'?: string;
  Latitude?: number;
  Longitude?: number;
  Website?: string;
  Domain?: string;
  'Opening hours'?: string;
  'Featured image'?: string;
  Cid?: string;
  'Place Id'?: string;
  Kgmid?: string;
  'Plus code'?: string;
  'Google Knowledge URL'?: string;
  Email?: string;
  'Social Medias'?: string;
  Facebook?: string;
  Instagram?: string;
  Twitter?: string;
  Yelp?: string;
  [key: string]: any; // Allow other potential columns
}

const DataImport: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<LeadData[]>([]);
  const [uniqueLeads, setUniqueLeads] = useState<LeadData[]>([]);
  const [duplicateCount, setDuplicateCount] = useState<number>(0);
  const [extractedCategories, setExtractedCategories] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    isValid: boolean;
    missingColumns: string[];
    rowCount: number;
  }>({ isValid: false, missingColumns: [], rowCount: 0 });
  const [importCount, setImportCount] = useState<number>(0);
  const [importStats, setImportStats] = useState<{total: number, session: number}>({total: 0, session: 0});
  const [isLoadingStats, setIsLoadingStats] = useState<boolean>(false);

  // Fetch Lead Stats on Component Mount and after successful imports
  useEffect(() => {
    fetchLeadStats();
  }, [importCount]);

  // Fetch lead stats from the database
  const fetchLeadStats = async () => {
    setIsLoadingStats(true);
    try {
      const response = await axios.get(`${config.apiBaseUrl}/leads/stats.php`);
      if (response.data.success) {
        setImportStats({
          total: response.data.total_count || 0,
          session: response.data.session_count || 0
        });
      } else {
        console.error('Failed to fetch lead stats:', response.data.message);
      }
    } catch (error) {
      console.error('Error fetching lead stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  // Clear state when file is removed or component unmounts
  const resetState = () => {
    setFile(null);
    setParsedData([]);
    setUniqueLeads([]);
    setDuplicateCount(0);
    setExtractedCategories(new Set());
    setError('');
    setIsProcessing(false);
    setIsExporting(false);
    setValidationResult({ isValid: false, missingColumns: [], rowCount: 0 });
  };

  // Validate and Parse File
  const validateAndParseFile = async (selectedFile: File) => {
    resetState();
    setFile(selectedFile);
    setIsProcessing(true);
    setError('');

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet) as LeadData[];
          setParsedData(jsonData);

          // --- Extract Unique Categories --- 
          const categoriesInFile = new Set<string>();
          jsonData.forEach(lead => {
            const catsString = lead.Categories || '';
            catsString.split(',')
              .map(cat => cat.trim())
              .filter(Boolean) // Remove empty strings
              .forEach(cat => categoriesInFile.add(cat));
          });
          setExtractedCategories(categoriesInFile);
          // --- End Category Extraction ---

          // Validate columns
          if (jsonData.length > 0) {
            const firstRow = jsonData[0];
            const missingColumns = REQUIRED_COLUMNS.filter(
              column => !(column in firstRow)
            );
            const isValid = missingColumns.length === 0;
            setValidationResult({
              isValid,
              missingColumns,
              rowCount: jsonData.length
            });

            if (isValid) {
              // --- Check for Duplicates --- 
              const namesToCheck = jsonData.map(lead => lead.Name).filter(Boolean);
              if (namesToCheck.length > 0) {
                const duplicateCheckResponse = await axios.post(`${config.apiBaseUrl}/leads/check_duplicates.php`, {
                  names: namesToCheck
                });

                if (duplicateCheckResponse.data.success) {
                  const existingNames = new Set(duplicateCheckResponse.data.existing_names);
                  const unique = jsonData.filter(lead => !existingNames.has(lead.Name));
                  setUniqueLeads(unique);
                  setDuplicateCount(jsonData.length - unique.length);
                  setError(''); // Clear previous errors if validation and check succeed
                } else {
                  setError(`Error checking duplicates: ${duplicateCheckResponse.data.message || 'Unknown error'}`);
                  setUniqueLeads([]); // Clear leads if check fails
                  setDuplicateCount(0);
                }
              } else {
                 // No names to check, all are considered unique for now
                 setUniqueLeads(jsonData);
                 setDuplicateCount(0);
              }
              // --- End Duplicate Check ---
            } else {
              setError(`Missing required columns: ${missingColumns.join(', ')}`);
              setUniqueLeads([]);
              setDuplicateCount(0);
            }
          } else {
            setError('The file is empty');
            setValidationResult({ isValid: false, missingColumns: [], rowCount: 0 });
            setUniqueLeads([]);
            setDuplicateCount(0);
          }
        } catch (parseError: any) {
          setError(`Error processing file content: ${parseError.message || 'Invalid file format?'}`);
          setValidationResult({ isValid: false, missingColumns: [], rowCount: 0 });
          setUniqueLeads([]);
          setDuplicateCount(0);
          setExtractedCategories(new Set());
        }
        setIsProcessing(false);
      };
      reader.onerror = () => {
        setError('Error reading the file.');
        setIsProcessing(false);
      };
      reader.readAsArrayBuffer(selectedFile);
    } catch (err) {
      setError('Could not initiate file reading.');
      setIsProcessing(false);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      validateAndParseFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop, 
    accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
    multiple: false
  });

  // Handle Export
  const handleExport = async () => {
    if (!uniqueLeads.length) {
      setError('No unique leads available to export');
      return;
    }

    setIsExporting(true);
    setError('');

    try {
      // Send only unique leads to the import endpoint
      const response = await axios.post(`${config.apiBaseUrl}/leads/import.php`, {
        leads: uniqueLeads 
      }, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.data.success) {
        alert(`Data exported successfully! ${response.data.success_count} leads imported, ${response.data.error_count} failed.`);
        
        // Increment import counter to trigger stats refresh
        setImportCount(prevCount => prevCount + 1);
        
        // Reset form after successful export
        resetState();
      } else {
        setError(response.data.message || 'Error exporting data');
        if (response.data.errors && Array.isArray(response.data.errors)) {
           console.error('Detailed import errors:', response.data.errors);
           setError(prev => `${prev} (${response.data.errors.length} details in console)`);
        }
      }
    } catch (err: any) {
      let errorMessage = 'Error exporting data to database: ';
      if (err.response) {
        errorMessage += err.response.data?.message || err.response.statusText;
      } else if (err.request) {
        errorMessage += 'No response from server.';
      } else {
        errorMessage += err.message;
      }
      setError(errorMessage);
      console.error("Export API error:", err);
    } finally {
      setIsExporting(false);
    }
  };

  // Render Component
  return (
    <div className="max-w-4xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Import Leads Data</h1>

      {/* Database Stats */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-medium text-gray-700 mb-2">Database Stats</h3>
        <div className="flex items-center justify-between">
          <div className="flex space-x-6">
            <div>
              <span className="text-sm text-gray-500">Total Leads</span>
              <p className="text-xl font-bold text-primary-700">{isLoadingStats ? '...' : importStats.total.toLocaleString()}</p>
            </div>
            {importStats.session > 0 && (
              <div>
                <span className="text-sm text-gray-500">Imported This Session</span>
                <p className="text-xl font-bold text-green-600">{isLoadingStats ? '...' : importStats.session.toLocaleString()}</p>
              </div>
            )}
          </div>
          <button 
            onClick={fetchLeadStats}
            disabled={isLoadingStats}
            className="text-sm px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center"
          >
            {isLoadingStats ? 'Refreshing...' : 'Refresh Stats'}
          </button>
        </div>
      </div>

      {/* Dropzone */}
      <div 
        {...getRootProps()} 
        className={`p-10 border-2 border-dashed rounded-lg text-center cursor-pointer ${isDragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300'}`}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>Drop the file here ...</p>
        ) : (
          <p>Drag 'n' drop an XLSX file here, or click to select file</p>
        )}
      </div>

      {/* File Info, Validation & Duplicate Status */}
      {file && (
        <div className="mt-4 space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium">Selected File:</h3>
            <p className="text-sm text-gray-600">{file.name}</p>
            {isProcessing && <p className="text-sm text-blue-600 mt-2">Processing...</p>}
          </div>
          
          {!isProcessing && validationResult.isValid && (
            <div className="p-4 bg-green-50 rounded-lg space-y-2">
              <p className="text-green-700 font-medium">
                ✓ Spreadsheet is valid. Found {validationResult.rowCount} total leads.
              </p>
              {duplicateCount > 0 && (
                <p className="text-orange-600">
                  ! Found {duplicateCount} potential duplicate lead name(s) already in the database (will be excluded).
                </p>
              )}
              <p className="text-green-900">
                 Ready to export {uniqueLeads.length} unique lead(s).
              </p>
              
              {/* Display Extracted Categories */}
              {extractedCategories.size > 0 && (
                 <div className="pt-2 border-t border-green-200">
                    <p className="text-sm font-medium text-gray-700 mb-1">Extracted Categories ({extractedCategories.size}):</p>
                    <div className="flex flex-wrap gap-1">
                       {Array.from(extractedCategories).sort().map(category => (
                           <span key={category} className="inline-block bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded">
                               {category}
                           </span>
                       ))}
                    </div>
                 </div>
              )}
            </div>
          )}

          {!isProcessing && !validationResult.isValid && validationResult.missingColumns.length > 0 && (
             <div className="p-4 bg-red-50 rounded-lg">
               <p className="text-red-700">✗ Invalid Spreadsheet: Missing columns - {validationResult.missingColumns.join(', ')}</p>
             </div>
          )}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-lg">
          <p>{error}</p>
        </div>
      )}

      {/* Preview Table - Show first 5 unique leads */}
      {uniqueLeads.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-2">Preview Unique Leads (First 5)</h3>
          <div className="overflow-x-auto shadow rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {Object.keys(uniqueLeads[0]).map((key) => (
                    <th key={key} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{key}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {uniqueLeads.slice(0, 5).map((row, index) => (
                  <tr key={index}>
                    {Object.values(row).map((value, i) => (
                      <td key={i} className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{String(value)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Export Button */}
      {!isProcessing && validationResult.isValid && uniqueLeads.length > 0 && (
        <div className="mt-6 text-right">
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {isExporting ? 'Exporting...' : `Export ${uniqueLeads.length} Unique Leads to DB`}
          </button>
        </div>
      )}
    </div>
  );
};

export default DataImport; 